import { useEffect, useState } from 'react';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import { hasLoad } from '../../state/slices/configurationSlice';
import {
  getReturnToValue,
  IsInReviewEditMode,
} from '../wizard/wizard-component';

interface CarryingLoadComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const CarryingLoadComponent = ({
  navigationConfiguration,
}: CarryingLoadComponentProps) => {
  const dispatch = useAppDispatch();

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const [formError, setFormError] = useState('');
  const [selection, setSelection] = useState(
    stateCollision.collision?.vehicles![0].damage?.loadDetail
      ? stateCollision.collision?.vehicles![0].damage.loadDetail.toString()
      : '0',
  );

  const [nextPage, setNextPage] = useState('');

  const defaultSelection = '0';
  const errorText = 'Answer yes, no or not applicable in order to continue';
  const loadDetailUnloaded = 99;
  const LoadSpillageLoadNotSpilled = 101;

  const [attachmentDescription, setAttachmentDescription] = useState('');

  useEffect(() => {
    let pageConfig = 'error';

    if (
      stateCollision.collision.vehicles![0].damage?.damageSelection &&
      stateCollision.collision.vehicles![0].damage?.damageSelection.length == 1
    ) {
      if (hasLoad(parseInt(selection), stateConfiguration)) {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.yes !== undefined,
        )!.yes;
      } else if (!hasLoad(parseInt(selection), stateConfiguration)) {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.no !== undefined,
        )!.no; // this is also na case
      }
    } else if (
      stateCollision.collision.vehicles![0].damage?.damageSelection &&
      stateCollision.collision.vehicles![0].damage?.damageSelection.length > 1
    ) {
      if (hasLoad(parseInt(selection), stateConfiguration)) {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.yes !== undefined,
        )!.yes;
      } else if (!hasLoad(parseInt(selection), stateConfiguration)) {
        pageConfig = navigationConfiguration.pagingConfiguration.find(
          (item) => item.done !== undefined,
        )!.done; // this is also na case
      }
    }

    setNextPage(pageConfig);
  }, [selection]);

  useEffect(() => {
    const attachmentDescription =
      stateConfiguration.configuration.attachments.find((x) => {
        return (
          x.id ==
          parseInt(stateCollision.collision.vehicles![0].damage?.attachment!)
        );
      })?.value;
    setAttachmentDescription(attachmentDescription?.toLowerCase() ?? '');
  }, [stateCollision.collision.vehicles![0].damage?.attachment]);

  useEffect(() => {
    setSelection(
      stateCollision.collision.vehicles![0].damage?.loadDetail
        ? stateCollision.collision.vehicles![0].damage?.loadDetail!.toString()
        : defaultSelection,
    );
  }, [stateCollision.collision.vehicles![0].damage?.loadDetail]);

  const save = () => {
    if (selection) {
      let updatedCollision: Collision = { ...stateCollision.collision };

      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          false,
          true,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }
      updatedCollision.vehicles = [
        {
          ...stateCollision.collision.vehicles?.[0],
          damage: {
            ...stateCollision.collision.vehicles?.[0].damage,
            loadDetail: parseInt(selection),
            loadSpillage: hasLoad(parseInt(selection), stateConfiguration)
              ? stateCollision.collision.vehicles?.[0].damage?.loadSpillage
              : parseInt(selection) == loadDetailUnloaded
                ? LoadSpillageLoadNotSpilled
                : undefined,
          },
        },
      ];

      dispatch(updateCollision(updatedCollision));
    }
    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h1>Vehicle damage</h1>
      The collision details section is the starting point for drivers to
      identify the type of collision they were involved in. In some cases you’ll
      be prompted to add some additional details depending on your type of
      collision.
      <h2>Was your {attachmentDescription} carrying a load?</h2>
      Load means any content such as any materials, equipment, furniture, tools,
      etc.
      <GoASpacer vSpacing="m" />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          error={formError.length > 0}
          name="attachmentRadioGroup"
          value={selection}
          onChange={(selection, id) => {
            setSelection(id);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
          }}
        >
          <GoARadioItem
            value={stateConfiguration.configuration.loadDetails
              .find((x) => {
                return x.value.toLowerCase() == 'yes';
              })
              ?.id.toString()}
            label="Yes"
          ></GoARadioItem>
          <GoARadioItem
            value={stateConfiguration.configuration.loadDetails
              .find((x) => {
                return x.value.toLowerCase() == 'no';
              })
              ?.id.toString()}
            label="No"
          ></GoARadioItem>
          <GoARadioItem
            value={stateConfiguration.configuration.loadDetails
              .find((x) => {
                return x.value.toLowerCase() == 'not applicable';
              })
              ?.id.toString()}
            label="Not applicable"
          ></GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return defaultValidate(
            selection,
            defaultSelection,
            errorText,
            setFormError,
            navigationConfiguration.errorNotification,
          );
        }}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        showViewSummaryButton={!IsInReviewEditMode()}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
